import axios from "axios";
import { useEffect, useState } from "react";
import { FaUserEdit } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function EditCustomer() {
   const navigate = useNavigate();
   const { id } = useParams();

   const [form, setForm] = useState({
      accountNumber: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dob: "",
      country: "",
      address: "",
      city: "",
      borough: "",
      state: "NY",
      zip: "",
      payment_method: "",
      paymentContact: "",
      ach_account_name: '',
      ach_account_number: '',
      ach_routing_number: '',
      ach_account_type: '',
      stripe_account_id: ''
      // bankAccountNumber: "",
      // bankRoutingNumber: ""
   });

   const [originalArea, setOriginalArea] = useState({
      borough: "",
      zip: "",
      state: "NY"
   });

   const [areaStatus, setAreaStatus] = useState({ message: '', valid: false });

   useEffect(() => {
      const fetchCustomer = async () => {
         try {
            const res = await fetch(`/auth/users/customer/${id}`);
            const data = await res.json();
            if (res.ok) {
               setForm({
                  accountNumber: data.accountNumber || "",
                  firstName: data.first_name || "",
                  lastName: data.last_name || "",
                  email: data.email || "",
                  phone: data.phone || "",
                  dob: data.dob ? data.dob.split("T")[0] : "",
                  country: data.country || "",
                  address: data.address || "",
                  city: data.city || "",
                  borough: data.borough || "",
                  state: data.state || "NY",
                  zip: data.zip || "",
                  payment_method: data.payment_method || "",
                  paymentContact: data.paymentContact || "",
                  ach_account_name: data.ach_account_name || '',
                  ach_account_number: data.ach_account_number || '',
                  ach_routing_number: data.ach_routing_number || '',
                  ach_account_type: data.ach_account_type || '',
                  stripe_account_id: data.stripe_account_id || ''
                  // bankAccountNumber: data.bankAccountNumber || "",
                  // bankRoutingNumber: data.bankRoutingNumber || ""
               });

               setOriginalArea({
                  borough: data.borough || "",
                  zip: data.zip || "",
                  state: data.state || "NY"
               });
            } else {
               toast.error(data.message || "Failed to fetch customer.");
            }
         } catch (err) {
            console.error("Error fetching customer:", err);
            toast.error("Error fetching customer data.");
         }
      };

      fetchCustomer();
   }, [id]);

   const handleChange = (e) => {
      const { id, value } = e.target;
      setForm((prev) => ({ ...prev, [id]: value }));

      if (["borough", "zip", "state"].includes(id)) {
         setAreaStatus({ message: "", valid: false });
      }
   };

   const handleZipCheck = async () => {
      try {
         const res = await axios.post("/auth/check-service-area", {
            state: form.state,
            borough: form.borough,
            zipcode: form.zip,
         });
         setAreaStatus({ message: res.data.message, valid: res.data.available });
         toast.success(res.data.message);
      } catch (err) {
         const msg = err.response?.data?.message || "Error checking ZIP availability.";
         setAreaStatus({ message: msg, valid: false });
         toast.error(msg);
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      if (areaChanged && !areaStatus.valid) {
         toast.error('Please check ZIP availability before submitting.');
         return;
      }


      try {
         const res = await fetch(`/auth/users/customer/${id}`, {
            method: "PUT",
            headers: {
               "Content-Type": "application/json"
            },
            body: JSON.stringify(form)
         });

         const result = await res.json();

         if (res.ok) {
            toast.success("Customer updated successfully.");
            setTimeout(() => navigate("/redem-customerlist"), 1500);

         } else {
            toast.error(result.message || "Failed to update customer.");
         }
      } catch (err) {
         console.error("Update failed:", err);
         toast.error("Update failed. Please try again.");
      }
   };

   const areaChanged =
      form.borough !== originalArea.borough ||
      form.zip !== originalArea.zip ||
      form.state !== originalArea.state;

   return (
      <section className="min-h-screen bg-gray-50 flex lg:items-center lg:justify-center px-4 sm:px-6 lg:px-8 py-8">
         <div className="bg-white w-full max-w-4xl p-6 sm:p-8 rounded-2xl shadow-lg">
            <h1 className="text-3xl font-bold text-center text-green-700 mb-8 flex items-center justify-center gap-2">
               <FaUserEdit className="text-3xl" />
               Edit Customer
            </h1>

            <form className="space-y-6" onSubmit={handleSubmit}>
               <div>
                  <label className="block font-medium mb-1">Account Number <span className="text-red-500">*</span></label>
                  <input type="text" id="accountNumber" value={form.accountNumber} readOnly className="w-full p-3 rounded-md bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed" />
               </div>

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="firstName" label="First Name *" required value={form.firstName} onChange={handleChange} className="bg-lime-50" />
                  <Input id="lastName" label="Last Name *" required value={form.lastName} onChange={handleChange} className="bg-lime-50" />
               </div>

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="email" label="Email *" type="email" required value={form.email} onChange={handleChange} className="bg-lime-50" />
                  <Input id="phone" label="Phone Number *" required value={form.phone} onChange={handleChange} className="bg-lime-50" />
               </div>

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="dob" label="Date of Birth" type="date" value={form.dob} onChange={handleChange} className="bg-lime-50" />
                  <Select id="country" label="Country" value={form.country} onChange={handleChange} className="bg-lime-50" options={["India", "USA", "UK"]} />
               </div>

               <Input id="address" label="Street Address" value={form.address} onChange={handleChange} className="bg-lime-50" />

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="city" label="City" value={form.city} onChange={handleChange} className="bg-lime-50" />
                  <Select id="borough" label="Borough" value={form.borough} onChange={handleChange} className="bg-lime-50" options={["Bronx", "Queens", "Manhattan", "Brooklyn", "Staten Island"]} />
               </div>

               <div className="grid gap-4 sm:grid-cols-3 items-end">
                  <div>
                     <label htmlFor="state" className="block font-medium mb-1">State <span className="text-red-500">*</span></label>
                     <input type="text" id="state" name="state" value={form.state} readOnly className="p-3 bg-gray-100 rounded-md border border-gray-300 w-full text-gray-500 cursor-not-allowed" />
                  </div>
                  <Input id="zip" label="ZIP" value={form.zip} onChange={handleChange} className="bg-lime-50" />
                  <div>
                     <label className="block invisible mb-1">Check</label>
                     <button
                        type="button"
                        onClick={handleZipCheck}
                        disabled={!areaChanged}
                        className={`w-full h-[42px] px-4 rounded-md text-white 
                           ${areaChanged ? "bg-[#54BE73] hover:bg-green-700" : "bg-gray-300 cursor-not-allowed"}`}
                     >
                        Check Availability
                     </button>
                  </div>
               </div>

               {areaStatus.message && (
                  <div className={`text-sm ${areaStatus.valid ? "text-green-600" : "text-red-600"}`}>
                     {areaStatus.message}
                  </div>
               )}

               <Select
                  id="payment_method"
                  label="Payment Method"
                  value={form.payment_method}
                  onChange={handleChange}
                  className="bg-lime-50"
                  options={["Cash", "Ev-voucher", "Fuel-Voucher", "Stock-credit", "Check by Mail", "ACH", "PayPal"]}
               />

               {/* Show only if PayPal */}
               {form.payment_method === "PayPal" && (
                  <Input
                     id="paymentContact"
                     label="Email for PayPal *"
                     type="email"
                     required
                     value={form.paymentContact}
                     onChange={handleChange}
                     className="bg-lime-50"
                  />
               )}

               {/* Show only if ACH */}
               {form.payment_method === "ACH" && (
                  <>
                     <div className="grid gap-4 sm:grid-cols-2">
                        <Input id="ach_account_name" label="ACH Account Name *" type="text" required value={form.ach_account_name} onChange={handleChange} className="bg-lime-50" />
                        <Input id="ach_account_number" label="ACH Account Number *" type="text" required value={form.ach_account_number} onChange={handleChange} className="bg-lime-50" />
                     </div>
                     <div className="grid gap-4 sm:grid-cols-2">
                        <Input id="ach_routing_number" label="ACH Routing Number *" type="text" required value={form.ach_routing_number} onChange={handleChange} className="bg-lime-50" />
                        <Input id="ach_account_type" label="ACH Account Type *" type="text" required value={form.ach_account_type} onChange={handleChange} className="bg-lime-50" />
                     </div>
                     <Input
                        id="stripe_account_id" label="Stripe Account ID *" type="text" required value={form.stripe_account_id} onChange={handleChange} className="bg-lime-50"
                     />
                  </>
               )}

               <div className="flex justify-end gap-4 pt-2">
                  <button type="button" onClick={() => navigate("/redem-customerlist")} className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300">Cancel</button>
                  <button type="submit" className="px-6 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700">Update</button>
               </div>
            </form>

            <ToastContainer position="top-right" autoClose={3000} />
         </div>
      </section>
   );
}

function Input({ id, label, type = "text", required = false, value, onChange, readOnly = false, className = "", error = "" }) {
   return (
      <div>
         <label htmlFor={id} className="block mb-1 font-medium">{label}</label>
         <input
            id={id}
            name={id}
            type={type}
            required={required}
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            aria-required={required}
            aria-invalid={!!error}
            className={`w-full rounded-lg p-3 ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-green-600 ${className}`}
         />
         {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      </div>
   );
}

function Select({ id, label, value, onChange, options = [], className = "" }) {
   return (
      <div>
         <label htmlFor={id} className="block mb-1 font-medium">{label}</label>
         <select
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            className={`w-full rounded-lg p-3 ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-green-600 ${className}`}
         >
            <option value="">Select {label}</option>
            {options.map((opt) => (
               <option key={opt} value={opt}>{opt}</option>
            ))}
         </select>
      </div>
   );
}
